import { Box } from "@mui/material";
import type { TFunction } from "i18next";
import { type FC, type KeyboardEvent, useRef } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../components/UIToolkit/Badges/MetaChip";
import { radioGroupTargetIndex } from "../../../components/UIToolkit/Clickable/radioGroupKeys";
import { PrintoutPresetRow } from "../PrintoutPresetRow";
import { LIST_MAX_HEIGHT_PX, NEW_PRINTOUT_ROW_KEY } from "./consts";

type Props = {
	/** The rows in order: each saved printout, then the new printout's row while one is being designed. A summary is marked so. */
	rows: { key: string; name: string; isSummary: boolean }[];
	/** Id of the visible label naming the group. */
	labelId: string;
	selectedKey: string;
	/** The chosen row has unsaved work: marked "Not saved". */
	isDirty: boolean;
	locked: boolean;
	onSelect: (printoutId: string) => void;
};

/** "Not saved" on the chosen row with unsaved work (it says more than the kind), else "Summary" on a summary printout. */
const trailingChipOf = (row: Props["rows"][number], isUnsaved: boolean, t: TFunction) => {
	if (isUnsaved) return <MetaChip label={t("printout.custom.ai.unsaved")} tone="warning" dataTestId="custom-printout-unsaved-chip" />;
	return row.isSummary ? (
		<MetaChip label={t("printout.custom.ai.summary.chip")} tone="neutral" dataTestId={`custom-printout-summary-chip-${row.key}`} />
	) : undefined;
};

/**
 * The custom printouts as a radio group, drawn with the Detailed list's rows: arrow keys move and pick (roving
 * tab stop, wrapping at the ends), and the chosen row is marked "Not saved" while it has unsaved work.
 */
export const CustomPrintoutRadioRows: FC<Props> = ({ rows, labelId, selectedKey, isDirty, locked, onSelect }) => {
	const { t } = useTranslation();
	const radioRefs = useRef(new Map<string, HTMLDivElement>());
	const choose = (key: string): void => {
		// The new printout's row is only ever the chosen one; there's nothing to switch to.
		if (key !== NEW_PRINTOUT_ROW_KEY && key !== selectedKey) onSelect(key);
	};
	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
		const targetIndex = radioGroupTargetIndex(
			event.key,
			rows.findIndex((row) => row.key === selectedKey),
			rows.length,
			getComputedStyle(event.currentTarget).direction === "rtl",
		);
		if (targetIndex === null) return;
		event.preventDefault();
		if (locked) return;
		choose(rows[targetIndex].key);
		radioRefs.current.get(rows[targetIndex].key)?.focus();
	};
	return (
		<Box
			role="radiogroup"
			aria-labelledby={labelId}
			onKeyDown={handleKeyDown}
			sx={{ display: "flex", flexDirection: "column", gap: 0.5, maxHeight: LIST_MAX_HEIGHT_PX, overflowY: "auto" }}
		>
			{rows.map((row) => (
				<PrintoutPresetRow
					key={row.key}
					ref={(element) => {
						if (element) radioRefs.current.set(row.key, element);
						else radioRefs.current.delete(row.key);
					}}
					name={row.name}
					checked={row.key === selectedKey}
					tabbable={row.key === selectedKey}
					disabled={locked}
					onSelect={() => choose(row.key)}
					testIdKey={row.key}
					dataTestId={`custom-printout-row-${row.key}`}
					trailing={trailingChipOf(row, row.key === selectedKey && isDirty, t)}
				/>
			))}
		</Box>
	);
};
